// File: src/pages/ClosingStockReport.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Alert,
  Label,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

import {
  _Get,
  PurchaseOrderList,
  createOrderhistoryviewDATABASE,
} from "../import/ApiEndPoint/ApiCalling";
import {
  WareHouse_Closing_Stock,
  Create_Warehouse_List,
  PurchaseProductList_Product,
} from "../import/ApiEndPoint/Api";
import SuperAdminUI from "../import/SuperAdminUI";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => (Number.isFinite(+v) ? +v : 0);

const normId = (val) => {
  if (!val) return "";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    if (val._id) return String(val._id);
    if (val.$oid) return String(val.$oid);
  }
  return String(val);
};

/* ================= Financial Year Helpers ================= */

const getFYStartYear = (date = new Date()) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = d.getMonth();
  return m >= 3 ? y : y - 1;
};

const getFinancialYearLabel = (date = new Date()) => {
  const start = getFYStartYear(date);
  return `${start}-${String(start + 1).slice(-2)}`;
};

const parseFYStart = (fy) => {
  const start = Number(String(fy || "").split("-")[0]);
  return Number.isFinite(start) ? start : getFYStartYear();
};

const getPreviousFY = (fy) => {
  const start = parseFYStart(fy);
  const prevStart = start - 1;
  return `${prevStart}-${String(prevStart + 1).slice(-2)}`;
};

const getSelectedFinancialYear = () =>
  localStorage.getItem("financialYearSelected") || getFinancialYearLabel();

const buildSelectedDatabase = (db, fy) =>
  `${db}-${fy || getSelectedFinancialYear()}`;

const safeJson = (v) => {
  if (!v) return null;
  if (typeof v === "object") return v;
  try {
    return JSON.parse(v);
  } catch {
    return null;
  }
};

const findFYEntry = (container, fy) => {
  const data = safeJson(container);
  if (!data) return null;

  if (Array.isArray(data)) {
    return (
      data.find(
        (x) =>
          String(
            x?.financialYear || x?.FinancialYear || x?.fy || x?.year || "",
          ) === String(fy),
      ) || null
    );
  }

  if (typeof data === "object") {
    return (
      data[fy] ||
      data[String(fy).replace("-", "_")] ||
      data[String(fy).replace("-", "/")] ||
      null
    );
  }

  return null;
};

const firstDefined = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
};

const getOpeningFinancialYear = (row = {}) =>
  firstDefined(
    row?._productDetailsFromProductList?.openingFinancialYear,
    row?._productDetailsFromProductList?.OpeningFinancialYear,
    row?._productDetailsFromProductList?.financialYearOpeningYear,
    row?._productDetailsFromProductList?.openingFY,
    row?._productDetailsFromProductList?.OpeningFY,
    row?._productDetailsFromProductList?.openingYear,
    row?.openingFinancialYear,
    row?.OpeningFinancialYear,
    row?.financialYearOpeningYear,
    row?.productId?.openingFinancialYear,
    row?.productId?.OpeningFinancialYear,
    row?.product?.openingFinancialYear,
    row?.product?.OpeningFinancialYear,
  );

const getProductSavedOpening = (row = {}) => {
  const product =
    row?._productDetailsFromProductList ||
    (row?.productId && typeof row.productId === "object"
      ? row.productId
      : {}) ||
    row?.product ||
    {};

  const qty = firstDefined(
    product?.Opening_Stock,
    product?.openingStock,
    product?.openingQty,
    product?.oQty,
    product?.financialYearOpeningStock,
    product?.OpeningStock,
    row?.Opening_Stock,
    row?.openingStock,
    row?.openingQty,
    row?.oQty,
  );

  const rate = firstDefined(
    product?.openingRate,
    product?.Opening_Rate,
    product?.OpeningRate,
    product?.oRate,
    product?.purchaseRate,
    product?.PurchaseRate,
    product?.Product_Purchase_Rate,
    product?.Purchase_Price,
    product?.purchasePrice,
    product?.PurchasePrice,
    product?.costPrice,
    product?.Product_Rate,
    product?.price,
    row?.openingRate,
    row?.oRate,
    row?.price,
  );

  return {
    qty: num(qty),
    rate: num(rate),
    source: "Product saved opening",
  };
};

const extractBackendFYOpening = (row, fy) => {
  const directFY = String(
    row?.financialYear ||
      row?.openingFinancialYear ||
      row?.FinancialYear ||
      row?.fy ||
      "",
  );

  if (directFY && directFY === String(fy)) {
    const q = firstDefined(
      row?.financialYearOpeningStock,
      row?.openingStock,
      row?.openingQty,
      row?.Opening_Stock,
      row?.oQty,
    );

    const r = firstDefined(
      row?.financialYearOpeningRate,
      row?.openingRate,
      row?.oRate,
    );

    if (q !== undefined || r !== undefined) {
      return {
        qty: num(q),
        rate: num(r),
        source: "Backend FY opening",
      };
    }
  }

  const possible = [
    row?.openingStockByFY,
    row?.openingStocks,
    row?.financialYearOpenings,
    row?.openingByFinancialYear,
    row?.fyOpeningStock,
    row?.yearWiseOpeningStock,
    row?.productId?.openingStockByFY,
    row?.productId?.openingStocks,
    row?.productId?.financialYearOpenings,
    row?.productId?.openingByFinancialYear,
  ];

  for (const item of possible) {
    const entry = findFYEntry(item, fy);
    if (entry) {
      const q = firstDefined(
        entry?.openingStock,
        entry?.openingQty,
        entry?.Opening_Stock,
        entry?.oQty,
        entry?.qty,
        entry?.closingQty,
      );

      const r = firstDefined(
        entry?.openingRate,
        entry?.rate,
        entry?.closingAveRate,
        entry?.closingRate,
      );

      return {
        qty: num(q),
        rate: num(r),
        source: "Backend FY opening",
      };
    }
  }

  return null;
};

const stockSnapshotKey = (db, fy) => `FY_CLOSING_STOCK_SNAPSHOT:${db}:${fy}`;

const getProductIdForKey = (row) =>
  normId(
    row?._productIdResolved ||
      row?._productDetailsFromProductList?._id ||
      row?._productDetailsFromProductList?.id ||
      row?.productId?._id ||
      row?.productId ||
      row?.pId ||
      row?.ProductId ||
      row?.product?._id ||
      "",
  );

const getWarehouseIdForKey = (row) =>
  normId(
    row?._warehouseIdResolved ||
      row?.warehouseId ||
      row?.warehouse ||
      row?.warehouse?._id ||
      row?.warehouseId1 ||
      "",
  );

const getProductTitleForKey = (row = {}) =>
  String(
    row?.Product_Title ||
      row?._productDetailsFromProductList?.Product_Title ||
      row?._productDetailsFromProductList?.productName ||
      row?.productId?.Product_Title ||
      row?.productName ||
      row?.ProductName ||
      "",
  )
    .trim()
    .toLowerCase();

const makeStockKey = (row) => {
  const wid = getWarehouseIdForKey(row);
  const pid = getProductIdForKey(row);

  if (wid && pid) return `${wid}::${pid}`;

  const title = getProductTitleForKey(row);

  if (wid && title) return `${wid}::title:${title}`;

  if (pid) return `id:${pid}`;
  if (title) return `title:${title}`;

  return "";
};

const getStockLookupKeys = (row = {}) => {
  const keys = [];
  const wid = getWarehouseIdForKey(row);
  const pid = getProductIdForKey(row);
  const title = getProductTitleForKey(row);

  if (wid && pid) keys.push(`${wid}::${pid}`);
  if (wid && title) keys.push(`${wid}::title:${title}`);
  if (pid) keys.push(`id:${pid}`);
  if (title) keys.push(`title:${title}`);

  return [...new Set(keys.filter(Boolean))];
};

const addPreviousClosingToMap = (map, row, closing) => {
  const keys = getStockLookupKeys(row);
  if (!keys.length) return;

  const payload = {
    closingQty: num(closing?.closingQty),
    closingAveRate: num(closing?.closingAveRate),
    closingTotal: num(closing?.closingTotal),
  };

  keys.forEach((key) => {
    if (!map[key]) {
      map[key] = { ...payload };
      return;
    }

    // Product-only fallback keys can receive same product from multiple warehouses.
    // Sum qty/total and keep a weighted average rate so opening is still usable.
    const oldQty = num(map[key]?.closingQty);
    const oldTotal = num(map[key]?.closingTotal);
    const newQty = num(payload.closingQty);
    const newTotal = num(payload.closingTotal);
    const totalQty = oldQty + newQty;
    const totalValue = oldTotal + newTotal;

    map[key] = {
      closingQty: Number(totalQty.toFixed(2)),
      closingTotal: Number(totalValue.toFixed(2)),
      closingAveRate:
        totalQty > 0 ? Number((totalValue / totalQty).toFixed(2)) : 0,
    };
  });
};

const findPreviousClosingForRow = (row, previousClosingMap = {}) => {
  const keys = getStockLookupKeys(row);

  for (const key of keys) {
    if (previousClosingMap?.[key]) return previousClosingMap[key];
  }

  return null;
};

const getProductTitleKey = (item = {}) =>
  String(
    item?.Product_Title ||
      item?.productId?.Product_Title ||
      item?.productName ||
      item?.ProductName ||
      item?.name ||
      "",
  )
    .trim()
    .toLowerCase();

const getProductWarehouseId = (item = {}) =>
  normId(
    item?.warehouse?._id ||
      item?.warehouse ||
      item?.warehouseId ||
      item?.warehouseId1 ||
      item?.WarehouseId ||
      "",
  );

const buildProductDetailsMap = (products = []) => {
  const byId = new Map();
  const byTitle = new Map();
  const byWarehouseProductId = new Map();
  const byWarehouseTitle = new Map();

  (Array.isArray(products) ? products : []).forEach((product) => {
    const id = normId(product?._id || product?.id || product?.productId || "");
    const title = getProductTitleKey(product);
    const warehouseId = getProductWarehouseId(product);

    if (id) byId.set(id, product);
    if (title && !byTitle.has(title)) byTitle.set(title, product);
    if (warehouseId && id)
      byWarehouseProductId.set(`${warehouseId}::${id}`, product);
    if (warehouseId && title)
      byWarehouseTitle.set(`${warehouseId}::${title}`, product);
  });

  return {
    byId,
    byTitle,
    byWarehouseProductId,
    byWarehouseTitle,
    list: products,
  };
};

const findProductDetailsForRow = (row, productDetailsMap) => {
  if (!productDetailsMap) return null;

  const productId = getProductIdForKey(row);
  const warehouseId = getWarehouseIdForKey(row);
  const title = getProductTitleKey(row);

  if (warehouseId && productId) {
    const byWarehouseProductId = productDetailsMap.byWarehouseProductId?.get(
      `${warehouseId}::${productId}`,
    );
    if (byWarehouseProductId) return byWarehouseProductId;
  }

  if (productId) {
    const byId = productDetailsMap.byId?.get(String(productId));
    if (byId) return byId;
  }

  if (warehouseId && title) {
    const byWarehouseTitle = productDetailsMap.byWarehouseTitle?.get(
      `${warehouseId}::${title}`,
    );
    if (byWarehouseTitle) return byWarehouseTitle;
  }

  if (title) {
    const byTitle = productDetailsMap.byTitle?.get(title);
    if (byTitle) return byTitle;
  }

  return null;
};

const mergeProductDetailsIntoRow = (row, productDetailsMap) => {
  const product = findProductDetailsForRow(row, productDetailsMap);
  if (!product) return row;

  return {
    ...row,
    _productDetailsFromProductList: product,
    _productIdResolved: normId(product?._id || product?.id || row?.productId),
    productId: row?.productId || product?._id,
    Product_Title:
      row?.Product_Title ||
      row?.productId?.Product_Title ||
      product?.Product_Title ||
      product?.productName ||
      "",
    HSN_Code:
      row?.HSN_Code ||
      row?.productId?.HSN_Code ||
      product?.HSN_Code ||
      product?.hsn ||
      "",
    openingFinancialYear: firstDefined(
      product?.openingFinancialYear,
      product?.OpeningFinancialYear,
      product?.financialYearOpeningYear,
      product?.openingFY,
      product?.OpeningFY,
      product?.openingYear,
      row?.openingFinancialYear,
      row?.OpeningFinancialYear,
    ),
    Opening_Stock: firstDefined(
      product?.Opening_Stock,
      product?.openingStock,
      product?.openingQty,
      row?.Opening_Stock,
      row?.openingStock,
      row?.oQty,
    ),
    openingRate: firstDefined(
      product?.openingRate,
      product?.Opening_Rate,
      product?.OpeningRate,
      product?.oRate,
      product?.purchaseRate,
      product?.PurchaseRate,
      product?.Product_Purchase_Rate,
      product?.Purchase_Price,
      product?.purchasePrice,
      product?.PurchasePrice,
      product?.costPrice,
      product?.Product_Rate,
      row?.openingRate,
      row?.oRate,
    ),
  };
};

const loadPreviousSnapshotOpening = (db, fy, row) => {
  try {
    const prevFY = getPreviousFY(fy);
    const raw = localStorage.getItem(stockSnapshotKey(db, prevFY));
    const snap = raw ? JSON.parse(raw) : {};
    const item = findPreviousClosingForRow(row, snap);

    if (!item) return null;

    return {
      qty: num(item?.closingQty),
      rate: num(item?.closingAveRate),
      source: `Previous FY closing snapshot (${prevFY})`,
    };
  } catch {
    return null;
  }
};

const getOpeningForFY = (row, fy, db, previousClosingMap = {}) => {
  const openingFY = getOpeningFinancialYear(row);
  const selectedStart = parseFYStart(fy);
  const openingStart = openingFY ? parseFYStart(openingFY) : null;
  const productOpening = getProductSavedOpening(row);

  // Product opening belongs to a future FY, so it must not appear in older reports.
  if (openingFY && openingStart > selectedStart) {
    return {
      qty: 0,
      rate: 0,
      source: `Product opening FY ${openingFY} is after selected FY`,
    };
  }

  // If product's openingFinancialYear is the selected FY,
  // opening must come directly from the product master opening information.
  if (openingFY && openingStart === selectedStart) {
    return {
      qty: num(productOpening?.qty),
      rate: num(productOpening?.rate),
      source: `Product opening (${openingFY})`,
    };
  }

  // If product opening belongs to an older FY,
  // selected FY opening must be previous FY closing.
  if (openingFY && openingStart < selectedStart) {
    const previousFromApi = findPreviousClosingForRow(row, previousClosingMap);

    if (previousFromApi) {
      return {
        qty: num(previousFromApi?.closingQty),
        rate: num(previousFromApi?.closingAveRate),
        source: `Previous FY closing (${getPreviousFY(fy)})`,
      };
    }

    const previous = loadPreviousSnapshotOpening(db, fy, row);
    if (previous) return previous;

    return {
      qty: 0,
      rate: 0,
      source: `Previous FY closing not found (${getPreviousFY(fy)})`,
    };
  }

  const backend = extractBackendFYOpening(row, fy);
  if (backend) return backend;

  if (productOpening?.qty || productOpening?.rate) {
    return {
      qty: num(productOpening?.qty),
      rate: num(productOpening?.rate),
      source: "Product saved opening",
    };
  }

  return {
    qty: num(row?.oQty ?? row?.openingQty ?? row?.Opening_Stock),
    rate: num(row?.openingRate ?? row?.oRate),
    source: "Legacy product opening",
  };
};

const normalizeRowsFromResponse = (res) => {
  if (Array.isArray(res?.data)) return res.data;

  if (!Array.isArray(res?.Warehouse)) return [];

  const out = [];

  res.Warehouse.forEach((wh) => {
    if (Array.isArray(wh?.productItems)) {
      wh.productItems.forEach((it, index) => {
        const productObj =
          it?.productId && typeof it.productId === "object" ? it.productId : {};

        const openingQty = firstDefined(
          it?.oQty,
          it?.openingQty,
          it?.Opening_Stock,
        );

        const openingRate = firstDefined(
          it?.openingRate,
          it?.oRate,
          it?.price,
          it?.cRate,
        );

        out.push({
          ...it,
          _id: it?._id || `${wh?._id || wh?.warehouseId}-${index}`,
          warehouseId: wh?.warehouseId || wh?._id || wh?.warehouseId1,
          warehouseName: wh?.warehouseName || "",
          Product_Title:
            it?.Product_Title ||
            productObj?.Product_Title ||
            it?.productName ||
            "",
          HSN_Code:
            it?.HSN_Code || productObj?.HSN_Code || productObj?.hsn || "",
          productId: it?.productId,
          date: wh?.date || it?.date || wh?.createdAt || it?.createdAt,

          oQty: openingQty,
          openingRate,
          openingCombineTotal:
            it?.openingCombineTotal ?? num(openingQty) * num(openingRate),

          pQty: it?.pQty ?? it?.purchaseQty ?? 0,
          avgPurchaseRate: it?.avgPurchaseRate ?? it?.pRate ?? 0,
          totalPurchaseData:
            it?.totalPurchaseData ?? it?.pTotal ?? it?.pBAmount ?? 0,

          pendingStock: it?.pendingStock ?? 0,
          pendingStockTotal: it?.pendingStockTotal ?? 0,

          sQty: it?.sQty ?? it?.saleQty ?? 0,
          sRate: it?.sRate ?? 0,
          sTotal: it?.sTotal ?? 0,

          closingQty: it?.closingQty ?? it?.cQty ?? it?.currentStock,
          closingAveRate: it?.closingAveRate ?? it?.cRate ?? it?.price,
          closingTotal: it?.closingTotal ?? it?.cTotal ?? it?.totalPrice,
        });
      });
    } else {
      out.push(wh);
    }
  });

  return out;
};

const injectOnce = () => {
  if (document.getElementById("wh-inline-css")) return;

  const style = document.createElement("style");
  style.id = "wh-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1300px; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tfoot td{
      padding:8px 10px; font-weight:800; background:#f8fafc; border-top:1px solid #e5e7eb; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }
    .wh-help{
      background:#fff7ed;
      color:#9a3412;
      border:1px solid #fed7aa;
      padding:6px 10px;
      border-radius:12px;
      font-size:12px;
      font-weight:700;
    }
  `;
  document.head.appendChild(style);
};

export default function ClosingStockReport() {
  const [selectedFY, setSelectedFY] = useState(getSelectedFinancialYear());
  const [scopeDb, setScopeDb] = useState("");

  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [rows, setRows] = useState([]);

  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("ALL");

  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [masterShow, setMasterShow] = useState(false);

  const [, setProductOutwardStockMap] = useState({});
  const [hideZeroClosingTotal, setHideZeroClosingTotal] = useState(false);

  useEffect(() => injectOnce(), []);

  const getCurrentScope = () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const role = user?.rolename?.roleName;

    let db = user?.database || "";
    let uid = user?._id || "";

    if (role === "MASTER") {
      const raw = localStorage.getItem("SuperadminIdByMaster");

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          const text = String(parsed || "");
          const parts = text.split(" ");
          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        } catch {
          const text = String(raw || "").replace(/^"|"$/g, "");
          const parts = text.split(" ");
          if (parts[0]) uid = parts[0];
          if (parts[1]) db = parts[1];
        }
      }
    }

    return { user, db, uid, role };
  };

  const loadWarehouses = async (db) => {
    const resp = await _Get(Create_Warehouse_List, db);
    const list = Array.isArray(resp?.Warehouse) ? resp.Warehouse : [];
    setWarehouses(list);

    const mp = new Map(
      list.map((w) => [String(w?._id), w?.warehouseName || ""]),
    );

    return { list, map: mp };
  };

  const loadProductDetailsMap = async (db) => {
    const res = await _Get(PurchaseProductList_Product, db);
    const productList = Array.isArray(res?.Product) ? res.Product : [];
    return buildProductDetailsMap(productList);
  };

  const getInvoiceItemProductId = (it = {}) =>
    normId(
      it?.productId?._id ||
        it?.productId?.id ||
        it?.productId ||
        it?.ProductId ||
        it?.product_id ||
        it?.product?._id ||
        "",
    );

  const getInvoiceItemTitle = (it = {}) =>
    String(
      it?.productId?.Product_Title ||
        it?.productData?.Product_Title ||
        it?.product?.Product_Title ||
        it?.Product_Title ||
        it?.productName ||
        it?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

  const pendingStockStatuses = [
    "pending",
    "billing",
    "dispatch",
    "delivery boy assigned",
    "out for delivery",
  ];

  const isPendingStockStatus = (status) => {
    const s = String(status || "")
      .trim()
      .toLowerCase();
    if (!s || s.includes("cancel") || s.includes("completed")) return false;
    return pendingStockStatuses.some((code) => s.includes(code));
  };

  const buildStockMapFromInvoices = (invoices = [], statusFilter = null) => {
    const bucket = {};

    const addToBucket = (key, qty, price) => {
      if (!key || qty <= 0) return;

      if (!bucket[key]) {
        bucket[key] = {
          qty: 0,
          amount: 0,
          priceSum: 0,
          priceCount: 0,
        };
      }

      bucket[key].qty += qty;
      bucket[key].amount += qty * price;
      bucket[key].priceSum += price;
      bucket[key].priceCount += 1;
    };

    invoices.forEach((invoice) => {
      const status = String(invoice?.status || "").toLowerCase();

      if (statusFilter && !statusFilter(status)) return;

      const items = Array.isArray(invoice?.orderItems)
        ? invoice.orderItems
        : [];

      items.forEach((it) => {
        const qty = num(
          it?.qty ?? it?.quantity ?? it?.Qty ?? it?.productQty ?? it?.saleQty,
        );

        const price = num(
          it?.price ??
            it?.rate ??
            it?.salesRate ??
            it?.saleRate ??
            it?.productId?.price ??
            it?.productId?.salesRate ??
            it?.productId?.saleRate,
        );

        const pid = getInvoiceItemProductId(it);
        const title = getInvoiceItemTitle(it);

        addToBucket(`id:${pid}`, qty, price);
        if (title) addToBucket(`title:${title}`, qty, price);
      });
    });

    const finalMap = {};

    Object.keys(bucket).forEach((key) => {
      const x = bucket[key];
      const avgRate =
        x.qty > 0
          ? x.amount / x.qty
          : x.priceCount > 0
            ? x.priceSum / x.priceCount
            : 0;

      finalMap[key] = {
        qty: Number(x.qty.toFixed(2)),
        rate: Number(avgRate.toFixed(2)),
        total: Number(x.amount.toFixed(2)),
      };
    });

    return finalMap;
  };

  const isCompletedPurchaseInvoiceStatus = (status) =>
    String(status || "")
      .trim()
      .toLowerCase()
      .includes("completed");

  const getPurchaseItemRate = (it = {}) =>
    num(
      it?.price ??
        it?.basicPrice ??
        it?.rate ??
        it?.purchaseRate ??
        it?.PurchaseRate ??
        it?.productData?.purchaseRate ??
        it?.productData?.PurchaseRate ??
        it?.productData?.price ??
        it?.productId?.purchaseRate ??
        it?.productId?.PurchaseRate ??
        it?.productId?.price,
    );

  const buildInwardStockMapFromPurchaseInvoices = (invoices = []) => {
    const bucket = {};

    const addToBucket = (key, qty, rate) => {
      if (!key || qty <= 0) return;

      if (!bucket[key]) {
        bucket[key] = {
          qty: 0,
          rateSum: 0,
          rateCount: 0,
        };
      }

      bucket[key].qty += qty;

      // Inward Rate = simple average of rates from all completed purchase invoices.
      if (rate > 0) {
        bucket[key].rateSum += rate;
        bucket[key].rateCount += 1;
      }
    };

    invoices.forEach((invoice) => {
      if (!isCompletedPurchaseInvoiceStatus(invoice?.status)) return;

      const items = Array.isArray(invoice?.orderItems)
        ? invoice.orderItems
        : [];

      items.forEach((it) => {
        const qty = num(
          it?.qty ?? it?.quantity ?? it?.Qty ?? it?.productQty ?? it?.pQty,
        );

        const rate = getPurchaseItemRate(it);
        const pid = getInvoiceItemProductId(it);
        const title = getInvoiceItemTitle(it);

        if (pid) addToBucket(`id:${pid}`, qty, rate);
        if (title) addToBucket(`title:${title}`, qty, rate);
      });
    });

    const finalMap = {};

    Object.keys(bucket).forEach((key) => {
      const x = bucket[key];
      const avgRate = x.rateCount > 0 ? x.rateSum / x.rateCount : 0;

      finalMap[key] = {
        qty: Number(x.qty.toFixed(2)),
        rate: Number(avgRate.toFixed(2)),
        total: Number((x.qty * avgRate).toFixed(2)),
      };
    });

    return finalMap;
  };

  const getPurchaseInvoiceListFromDatabase = async (db, fy) => {
    const selectedDatabase = buildSelectedDatabase(db, fy);
    const { uid } = getCurrentScope();
    const res = await PurchaseOrderList(uid, selectedDatabase);

    return Array.isArray(res?.orderHistory) ? res.orderHistory : [];
  };

  const loadInwardStockMap = async (db, fy) => {
    try {
      if (!db) return {};

      const invoices = await getPurchaseInvoiceListFromDatabase(db, fy);
      return buildInwardStockMapFromPurchaseInvoices(invoices);
    } catch (e) {
      console.error("[ClosingStockReport] inward stock load error:", e);
      return {};
    }
  };

  const findInwardStockForRow = (row, inwardMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = inwardMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = inwardMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const getSalesInvoiceListFromDatabase = async (db, fy) => {
    const selectedDatabase = buildSelectedDatabase(db, fy);
    const res = await createOrderhistoryviewDATABASE(selectedDatabase);

    return [
      ...(Array.isArray(res?.b2bData) ? res.b2bData : []),
      ...(Array.isArray(res?.b2csData) ? res.b2csData : []),
      ...(Array.isArray(res?.b2clData) ? res.b2clData : []),
      ...(Array.isArray(res?.orderHistory) ? res.orderHistory : []),
    ];
  };

  const loadPendingStockMap = async (db, fy) => {
    try {
      if (!db) return {};

      const invoices = await getSalesInvoiceListFromDatabase(db, fy);

      return buildStockMapFromInvoices(invoices, isPendingStockStatus);
    } catch (e) {
      console.error("[ClosingStockReport] pending stock load error:", e);
      return {};
    }
  };

  const loadOutwardStockMap = async (db, fy, shouldSetState = true) => {
    try {
      if (!db) {
        if (shouldSetState) setProductOutwardStockMap({});
        return {};
      }

      const invoices = await getSalesInvoiceListFromDatabase(db, fy);

      const finalMap = buildStockMapFromInvoices(
        invoices,
        (status) =>
          String(status || "")
            .trim()
            .toLowerCase() === "completed",
      );

      if (shouldSetState) setProductOutwardStockMap(finalMap);
      return finalMap;
    } catch (e) {
      console.error("[ClosingStockReport] outward stock load error:", e);
      if (shouldSetState) setProductOutwardStockMap({});
      return {};
    }
  };

  const findOutwardStockForRow = (row, outwardMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = outwardMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = outwardMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const findPendingStockForRow = (row, pendingMap) => {
    const pid = getProductIdForKey(row);

    if (pid) {
      const byId = pendingMap[`id:${String(pid)}`];
      if (byId) return byId;
    }

    const title = String(
      row?.Product_Title ||
        row?.productId?.Product_Title ||
        row?.productName ||
        row?.ProductName ||
        "",
    )
      .trim()
      .toLowerCase();

    if (title) {
      const byTitle = pendingMap[`title:${title}`];
      if (byTitle) return byTitle;
    }

    return { qty: 0, rate: 0, total: 0 };
  };

  const buildResolvedClosing = (row, opening, outward) => {
    const openingQty = num(opening?.qty);
    const openingRate = num(opening?.rate);
    const openingTotal = Number((openingQty * openingRate).toFixed(2));

    const inwardQty = num(row?.pQty);
    const inwardRate = num(row?.avgPurchaseRate);
    const inwardTotal = Number((inwardQty * inwardRate).toFixed(2));

    const pendingQty = num(row?.pendingStock);
    const outwardQty = num(outward?.qty);

    const closingQty = Number(
      (openingQty + inwardQty - pendingQty - outwardQty).toFixed(2),
    );

    // Closing Rate = (Opening Total + Inward Total) / (Opening Qty + Inward Qty)
    const totalAvailableQty = openingQty + inwardQty;
    const totalAvailableValue = openingTotal + inwardTotal;

    const closingAveRate =
      totalAvailableQty > 0 ? totalAvailableValue / totalAvailableQty : 0;

    // Closing Total = Closing Qty × Closing Rate
    const closingTotal = Number(
      (closingQty * closingAveRate.toFixed(2)).toFixed(2),
    );

    return {
      closingQty,
      closingAveRate: Number(closingAveRate.toFixed(2)),
      closingTotal,
    };
  };

  const resolveWarehouseForRow = (r, whMap) => {
    const idCandidate =
      r?.warehouseId ??
      r?.warehouse ??
      r?.warehouse?._id ??
      r?.warehouseId1 ??
      r?.productId?.warehouse ??
      r?.product?.warehouse ??
      r?.productId?.warehouseId ??
      r?.product?.warehouseId ??
      "";

    const wid = normId(idCandidate);
    const wname = r?.warehouseName || whMap.get(wid) || "";

    return {
      ...r,
      _warehouseIdResolved: wid,
      _warehouseNameResolved: wname,
    };
  };

  const loadPreviousClosingMap = async (
    db,
    fy,
    whMap,
    productDetailsMap,
    depth = 0,
  ) => {
    try {
      if (depth > 8) return {};

      const prevFY = getPreviousFY(fy);
      const dbParam = `${db}?financialYear=${encodeURIComponent(prevFY)}`;
      const res = await _Get(WareHouse_Closing_Stock, dbParam);
      const rawList = normalizeRowsFromResponse(res);

      const mergedRawList = rawList.map((r) =>
        mergeProductDetailsIntoRow(
          resolveWarehouseForRow(r, whMap),
          productDetailsMap,
        ),
      );

      const prevStart = parseFYStart(prevFY);
      const needsOlderPreviousClosing = mergedRawList.some((row) => {
        const openingFY = getOpeningFinancialYear(row);
        if (!openingFY) return false;
        return parseFYStart(openingFY) < prevStart;
      });

      const olderPreviousClosingMap = needsOlderPreviousClosing
        ? await loadPreviousClosingMap(
            db,
            prevFY,
            whMap,
            productDetailsMap,
            depth + 1,
          )
        : {};

      const outwardMap = await loadOutwardStockMap(db, prevFY, false);
      const pendingMap = await loadPendingStockMap(db, prevFY);
      const inwardMap = await loadInwardStockMap(db, prevFY);

      const map = {};

      mergedRawList.forEach((rowWithProduct) => {
        const key = makeStockKey(rowWithProduct);
        if (!key) return;

        const opening = getOpeningForFY(
          rowWithProduct,
          prevFY,
          db,
          olderPreviousClosingMap,
        );

        const outward = findOutwardStockForRow(rowWithProduct, outwardMap);
        const pending = findPendingStockForRow(rowWithProduct, pendingMap);
        const inward = findInwardStockForRow(rowWithProduct, inwardMap);
        const rowWithPending = {
          ...rowWithProduct,
          pQty: num(inward?.qty),
          avgPurchaseRate: num(inward?.rate),
          totalPurchaseData: num(inward?.total),
          pendingStock: num(pending?.qty),
          pendingStockTotal: num(pending?.total),
        };
        const closing = buildResolvedClosing(rowWithPending, opening, outward);

        addPreviousClosingToMap(map, rowWithProduct, closing);
      });

      return map;
    } catch (e) {
      console.warn("[ClosingStockReport] previous FY closing load error:", e);
      return {};
    }
  };

  const rebuildRows = async (db, fy) => {
    const dbParam = fy ? `${db}?financialYear=${encodeURIComponent(fy)}` : db;

    const res = await _Get(WareHouse_Closing_Stock, dbParam);

    const rawList = normalizeRowsFromResponse(res);

    const { map: whMap } = await loadWarehouses(db);
    const productDetailsMap = await loadProductDetailsMap(db);
    const previousClosingMap = await loadPreviousClosingMap(
      db,
      fy,
      whMap,
      productDetailsMap,
    );
    const outwardMap = await loadOutwardStockMap(db, fy);
    const pendingMap = await loadPendingStockMap(db, fy);
    const inwardMap = await loadInwardStockMap(db, fy);

    const withNames = rawList.map((r) => {
      const rowWithWH = resolveWarehouseForRow(r, whMap);
      const rowWithProduct = mergeProductDetailsIntoRow(
        rowWithWH,
        productDetailsMap,
      );
      const opening = getOpeningForFY(
        rowWithProduct,
        fy,
        db,
        previousClosingMap,
      );
      const outward = findOutwardStockForRow(rowWithProduct, outwardMap);
      const pending = findPendingStockForRow(rowWithProduct, pendingMap);
      const inward = findInwardStockForRow(rowWithProduct, inwardMap);
      const rowWithPending = {
        ...rowWithProduct,
        pQty: num(inward?.qty),
        avgPurchaseRate: num(inward?.rate),
        totalPurchaseData: num(inward?.total),
        pendingStock: num(pending?.qty),
        pendingStockTotal: num(pending?.total),
      };
      const closing = buildResolvedClosing(rowWithPending, opening, outward);

      const openingQty = num(opening?.qty);
      const openingRate = num(opening?.rate);

      return {
        ...rowWithPending,
        sQty: num(outward?.qty),
        closingQty: closing.closingQty,
        closingAveRate: closing.closingAveRate,
        closingTotal: closing.closingTotal,
        _openingQtyResolved: openingQty,
        _openingRateResolved: openingRate,
        _openingCombineTotalResolved: Number(
          (openingQty * openingRate).toFixed(2),
        ),
        _openingSourceResolved: opening?.source || "",
        _pendingRateResolved: num(pending?.rate),
        _avgSaleRateResolved: num(outward?.rate),
        _saleTotalResolved: num(outward?.total),
      };
    });

    const cleanRows = withNames.filter(
      (r) =>
        !r?._isSummary &&
        String(r?.warehouseName || "").toLowerCase() !== "total summary" &&
        String(
          r?.Product_Title || r?.productId?.Product_Title || "",
        ).toLowerCase() !== "total",
    );

    setRows(cleanRows);
  };

  const reload = async (fy = getSelectedFinancialYear()) => {
    const effectiveFY = fy || getSelectedFinancialYear();
    const { db, role } = getCurrentScope();

    setSelectedFY(effectiveFY);
    setScopeDb(db);

    if (role === "MASTER") setMasterShow(true);

    await rebuildRows(db, effectiveFY);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        await reload(getSelectedFinancialYear());
      } catch (e) {
        console.error("[ClosingStockReport] fetch error:", e);

        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load Closing Stock Report.",
        );

        setRows([]);
      } finally {
        setLoading(false);
      }
    })();

    // eslint-disable-next-line
  }, []);

  // Save current FY closing snapshot to localStorage.
  // This allows next FY opening to fall back to previous FY closing if backend has not yet been upgraded.
  useEffect(() => {
    if (!scopeDb || !selectedFY || !rows?.length) return;

    const snap = {};

    rows.forEach((r) => {
      const key = makeStockKey(r);
      if (!key) return;

      const payload = {
        productId: getProductIdForKey(r),
        warehouseId: getWarehouseIdForKey(r),
        productName: r?.Product_Title || r?.productId?.Product_Title || "",
        warehouseName: r?._warehouseNameResolved || "",
        closingQty: num(r?.closingQty ?? r?.currentStock),
        closingAveRate: num(r?.closingAveRate),
        closingTotal: num(r?.closingTotal),
        financialYear: selectedFY,
        savedAt: new Date().toISOString(),
      };

      getStockLookupKeys(r).forEach((lookupKey) => {
        snap[lookupKey] = payload;
      });
    });

    try {
      localStorage.setItem(
        stockSnapshotKey(scopeDb, selectedFY),
        JSON.stringify(snap),
      );
    } catch (e) {
      console.warn("Unable to save FY closing snapshot", e);
    }
  }, [rows, scopeDb, selectedFY]);

  const filtered = useMemo(() => {
    let data = rows;

    if (selectedWarehouseId !== "ALL") {
      data = data.filter(
        (r) =>
          String(r?._warehouseIdResolved || "") ===
            String(selectedWarehouseId) ||
          String(r?._warehouseNameResolved || "") ===
            String(
              warehouses.find(
                (w) => String(w?._id) === String(selectedWarehouseId),
              )?.warehouseName || "",
            ),
      );
    }

    if (startDate && endDate) {
      const inRange = data.filter((r) => {
        const d = r?.date ? String(r.date).split("T")[0] : "";
        return d >= startDate && d <= endDate;
      });

      if (inRange.length) {
        data = inRange;
      } else {
        const prev = data
          .filter((r) =>
            r?.date ? String(r.date).split("T")[0] < startDate : false,
          )
          .sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));

        if (prev.length) {
          const latest = String(prev[0].date).split("T")[0];
          data = prev.filter((r) => String(r.date).split("T")[0] === latest);
        } else {
          data = [];
        }
      }
    }

    if (hideZeroClosingTotal) {
      data = data.filter((r) => num(r?.closingTotal) !== 0);
    }

    const term = search.trim().toLowerCase();

    if (!term) return data;

    return data.filter((r) =>
      [
        r?._warehouseIdResolved,
        r?._warehouseNameResolved,
        r?.Product_Title || r?.productId?.Product_Title,
        r?.HSN_Code || r?.productId?.HSN_Code,
        r?._openingSourceResolved,
      ]
        .map(toStr)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [
    rows,
    selectedWarehouseId,
    startDate,
    endDate,
    search,
    warehouses,
    hideZeroClosingTotal,
  ]);

  const totals = useMemo(() => {
    const acc = {
      oQty: 0,
      openingRate: 0,
      openingCombineTotal: 0,
      pQty: 0,
      avgPurchaseRate: 0,
      totalPurchaseData: 0,
      pendingStock: 0,
      pendingRate: 0,
      pendingStockTotal: 0,
      sQty: 0,
      avgSaleRate: 0,
      saleTotal: 0,
      closingQty: 0,
      closingAveRate: 0,
      closingTotal: 0,
    };

    filtered.forEach((r) => {
      const pendingRate =
        num(r?._pendingRateResolved) ||
        (num(r?.pendingStock) > 0
          ? num(r?.pendingStockTotal) / num(r?.pendingStock)
          : 0);

      acc.oQty += num(r?._openingQtyResolved);
      acc.openingRate += num(r?._openingRateResolved);
      acc.openingCombineTotal += num(r?._openingCombineTotalResolved);

      acc.pQty += num(r?.pQty);
      acc.avgPurchaseRate += num(r?.avgPurchaseRate);
      acc.totalPurchaseData += num(r?.totalPurchaseData);

      acc.pendingStock += num(r?.pendingStock);
      acc.pendingRate += num(pendingRate);
      acc.pendingStockTotal += num(r?.pendingStockTotal);

      acc.sQty += num(r?.sQty);
      acc.avgSaleRate += num(r?._avgSaleRateResolved);
      acc.saleTotal += num(r?._saleTotalResolved);

      acc.closingQty += num(r?.closingQty ?? r?.currentStock);
      acc.closingAveRate += num(r?.closingAveRate);
      acc.closingTotal += num(r?.closingTotal);
    });

    return acc;
  }, [filtered]);

  const toFlatRow = (r) => ({
    "Financial Year": selectedFY,
    Warehouse: r?._warehouseNameResolved || r?._warehouseIdResolved || "",
    Product: r?.Product_Title || r?.productId?.Product_Title || "",
    HSN: r?.HSN_Code || r?.productId?.HSN_Code || "",
    "Opening Source": r?._openingSourceResolved || "",
    "Opening Qty": r?._openingQtyResolved ?? 0,
    "Opening Rate": num(r?._openingRateResolved).toFixed(2),
    "Opening Total": num(r?._openingCombineTotalResolved).toFixed(2),
    "Inward Qty": r?.pQty ?? 0,
    "Inward Rate (Avg)": num(r?.avgPurchaseRate).toFixed(2),
    "Inward Total": num(r?.totalPurchaseData).toFixed(2),
    "Pending Qty": r?.pendingStock ?? 0,
    "Pending Rate":
      num(r?._pendingRateResolved) > 0
        ? num(r?._pendingRateResolved).toFixed(2)
        : num(r?.pendingStock) > 0
          ? (num(r?.pendingStockTotal) / num(r?.pendingStock)).toFixed(2)
          : "0.00",
    "Pending Total": num(r?.pendingStockTotal).toFixed(2),
    "Sale Qty": r?.sQty ?? 0,
    "Sale Rate": num(r?._avgSaleRateResolved).toFixed(2),
    "Sale Total": num(r?._saleTotalResolved).toFixed(2),
    "Closing Qty": r?.closingQty ?? r?.currentStock ?? 0,
    "Closing Avg": num(r?.closingAveRate).toFixed(2),
    "Closing Total": num(r?.closingTotal).toFixed(2),
  });

  const exportXLSX = () => {
    const data = filtered.map(toFlatRow);

    data.push({
      "Financial Year": selectedFY,
      Warehouse: "GRAND TOTAL",
      Product: "",
      HSN: "",
      "Opening Source": "",
      "Opening Qty": totals.oQty,
      "Opening Rate": totals.openingRate.toFixed(2),
      "Opening Total": totals.openingCombineTotal.toFixed(2),
      "Inward Qty": totals.pQty,
      "Inward Rate (Avg)": totals.avgPurchaseRate.toFixed(2),
      "Inward Total": totals.totalPurchaseData.toFixed(2),
      "Pending Qty": totals.pendingStock,
      "Pending Rate": totals.pendingRate.toFixed(2),
      "Pending Total": totals.pendingStockTotal.toFixed(2),
      "Sale Qty": totals.sQty,
      "Sale Rate": totals.avgSaleRate.toFixed(2),
      "Sale Total": totals.saleTotal.toFixed(2),
      "Closing Qty": totals.closingQty,
      "Closing Avg": totals.closingAveRate.toFixed(2),
      "Closing Total": totals.closingTotal.toFixed(2),
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, "Closing Stock");

    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `ClosingStockReport-${selectedFY}.xlsx`,
    );
  };

  const handleParentSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setErrorMsg("");

    try {
      await reload(getSelectedFinancialYear());
    } catch (e1) {
      setErrorMsg(
        e1?.response?.data?.message ||
          e1?.message ||
          "Failed to load for selected DB",
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReload = async () => {
    setLoading(true);
    setErrorMsg("");

    try {
      await reload(getSelectedFinancialYear());
    } catch (e1) {
      setErrorMsg(
        e1?.response?.data?.message || e1?.message || "Failed to reload report",
      );

      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDropdownChange = (selectedValue) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(selectedValue));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filtered?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Closing Stock Report{" "}
                <span className="count-pill" title="records">
                  {filtered.length}
                </span>
              </div>

              {masterShow ? (
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 120px" }} />
              )}

              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Label className="mb-0" style={{ fontSize: 12 }}>
                  Warehouse
                </Label>
                <Input
                  type="select"
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  style={{ background: "#fff", height: 32 }}
                >
                  <option value="ALL">All Warehouses</option>
                  {warehouses.map((w) => (
                    <option key={w?._id} value={w?._id}>
                      {w?.warehouseName}
                    </option>
                  ))}
                </Input>
              </div>

              <div
                style={{
                  minWidth: 220,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    Start Date
                  </Label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>

                <div>
                  <Label className="mb-0" style={{ fontSize: 12 }}>
                    End Date
                  </Label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ height: 32 }}
                  />
                </div>

                <Button
                  color="primary"
                  onClick={handleReload}
                  style={{
                    height: 32,
                    fontWeight: 600,
                    textTransform: "uppercase",
                  }}
                >
                  Submit
                </Button>
              </div>

              <Button
                color={hideZeroClosingTotal ? "success" : "warning"}
                onClick={() => setHideZeroClosingTotal(true)}
                style={{ height: 32, fontWeight: 700 }}
              >
                Hide Zero Closing Total
              </Button>

              <Button
                color="secondary"
                onClick={() => setHideZeroClosingTotal(false)}
                style={{ height: 32, fontWeight: 700 }}
              >
                Show All
              </Button>

              <div
                onMouseEnter={() => setDownloadOpen(true)}
                onMouseLeave={() => setDownloadOpen(false)}
                style={{ position: "relative" }}
              >
                <ImDownload
                  style={{ cursor: "pointer" }}
                  title="download file"
                  size={24}
                  color="rgb(8, 91, 245)"
                />

                {downloadOpen && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: 32,
                      zIndex: 5,
                      border: "1px solid rgb(8, 91, 245)",
                      backgroundColor: "white",
                      borderRadius: 10,
                      overflow: "hidden",
                      minWidth: 130,
                      boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                    }}
                  >
                    <div
                      onClick={exportXLSX}
                      style={{
                        padding: "10px 12px",
                        cursor: "pointer",
                        fontWeight: 600,
                        letterSpacing: ".02em",
                      }}
                    >
                      .XLSX
                    </div>
                  </div>
                )}
              </div>

              <div className="wh-help">
                FY comes from financialYearSelected. Pending and outward stock
                come from Sales Invoice List database invoices. Opening source:
                Product openingFinancialYear same FY → Product opening.
                Otherwise → Previous FY closing.
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      <th rowSpan={2}>FY</th>
                      <th rowSpan={2}>WAREHOUSE</th>
                      <th colSpan={2}>PRODUCT DETAILS</th>
                      <th colSpan={4}>OPENING STOCK DETAILS</th>
                      <th colSpan={3}>INWARD STOCK DETAILS</th>
                      <th colSpan={3}>PENDING STOCK DETAILS</th>
                      <th colSpan={3}>OUTWARD STOCK DETAILS</th>
                      <th colSpan={3}>CLOSING STOCK DETAILS</th>
                    </tr>
                    <tr>
                      <th>PRODUCT</th>
                      <th>HSN</th>

                      <th>QTY</th>
                      <th>RATE</th>
                      <th>TOTAL</th>
                      <th>SOURCE</th>

                      <th>QTY</th>
                      <th>RATE</th>
                      <th>TOTAL</th>

                      <th>QTY</th>
                      <th>RATE</th>
                      <th>TOTAL</th>

                      <th>QTY</th>
                      <th>RATE</th>
                      <th>TOTAL</th>

                      <th>QTY</th>
                      <th>RATE</th>
                      <th>TOTAL</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.map((r, idx) => {
                      const pendingRate =
                        num(r?._pendingRateResolved) ||
                        (num(r?.pendingStock) > 0
                          ? num(r?.pendingStockTotal) / num(r?.pendingStock)
                          : 0);

                      return (
                        <tr
                          key={r?._id || r?.id || `${r?.Product_Title}-${idx}`}
                        >
                          <td>{selectedFY}</td>
                          <td>
                            {r?._warehouseNameResolved ||
                              r?._warehouseIdResolved ||
                              ""}
                          </td>

                          <td>
                            {r?.Product_Title ||
                              r?.productId?.Product_Title ||
                              ""}
                          </td>

                          <td>{r?.HSN_Code || r?.productId?.HSN_Code || ""}</td>

                          <td>{num(r?._openingQtyResolved).toFixed(2)}</td>
                          <td>{num(r?._openingRateResolved).toFixed(2)}</td>
                          <td>
                            {num(r?._openingCombineTotalResolved).toFixed(2)}
                          </td>
                          <td style={{ fontSize: 12, color: "#92400e" }}>
                            {r?._openingSourceResolved || ""}
                          </td>

                          <td>{num(r?.pQty).toFixed(2)}</td>
                          <td>{num(r?.avgPurchaseRate).toFixed(2)}</td>
                          <td>{num(r?.totalPurchaseData).toFixed(2)}</td>

                          <td>{num(r?.pendingStock).toFixed(2)}</td>
                          <td>{num(pendingRate).toFixed(2)}</td>
                          <td>{num(r?.pendingStockTotal).toFixed(2)}</td>

                          <td>{num(r?.sQty).toFixed(2)}</td>
                          <td>{num(r?._avgSaleRateResolved).toFixed(2)}</td>
                          <td>{num(r?._saleTotalResolved).toFixed(2)}</td>

                          <td>
                            {num(r?.closingQty ?? r?.currentStock).toFixed(2)}
                          </td>
                          <td>{num(r?.closingAveRate).toFixed(2)}</td>
                          <td>{num(r?.closingTotal).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr>
                      <td>{selectedFY}</td>
                      <td>GRAND TOTAL</td>
                      <td></td>
                      <td></td>

                      <td>{totals.oQty.toFixed(2)}</td>
                      <td>{totals.openingRate.toFixed(2)}</td>
                      <td>{totals.openingCombineTotal.toFixed(2)}</td>
                      <td></td>

                      <td>{totals.pQty.toFixed(2)}</td>
                      <td>{totals.avgPurchaseRate.toFixed(2)}</td>
                      <td>{totals.totalPurchaseData.toFixed(2)}</td>

                      <td>{totals.pendingStock.toFixed(2)}</td>
                      <td>{totals.pendingRate.toFixed(2)}</td>
                      <td>{totals.pendingStockTotal.toFixed(2)}</td>

                      <td>{totals.sQty.toFixed(2)}</td>
                      <td>{totals.avgSaleRate.toFixed(2)}</td>
                      <td>{totals.saleTotal.toFixed(2)}</td>

                      <td>{totals.closingQty.toFixed(2)}</td>
                      <td>{totals.closingAveRate.toFixed(2)}</td>
                      <td>{totals.closingTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
}
